import React, { useMemo, useState } from 'react';
import { Badge, Input, Spinner } from 'reactstrap';
import { useMutation } from '@apollo/client/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  UPDATE_ORDER_STATUS,
  UpdateOrderStatusData,
  UpdateOrderStatusVariables,
} from '../../graphql/mutations/orders';
import {
  BOOKING_STATUS_OPTIONS,
  formatStatusLabel,
  getStatusColor,
  normalizeOrderStatus,
} from '../../utils/bookingOrderHelpers';

const DESTRUCTIVE_STATUSES = new Set(['CANCELLED', 'FAILED', 'REFUNDED']);

interface BookingStatusSelectProps {
  orderId: number;
  status?: string | null;
  compact?: boolean;
}

const BookingStatusSelect: React.FC<BookingStatusSelectProps> = ({
  orderId,
  status,
  compact = true,
}) => {
  const { hasCapability, isSuperAdmin, isCustomerPortalUser } = useAuth();
  const canChange =
    !isCustomerPortalUser() &&
    (isSuperAdmin() || hasCapability('business_booking_change_status'));
  const current = normalizeOrderStatus(status);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [updateStatus, { loading }] = useMutation<
    UpdateOrderStatusData,
    UpdateOrderStatusVariables
  >(UPDATE_ORDER_STATUS, {
    refetchQueries: ['GetOrders', 'GetOrder'],
    awaitRefetchQueries: true,
  });

  const options = useMemo(() => {
    const list = [...BOOKING_STATUS_OPTIONS];
    if (current && !list.some((option) => option.value === current)) {
      list.unshift({ value: current, label: formatStatusLabel(current) });
    }
    return list;
  }, [current]);

  if (!canChange) {
    return (
      <Badge color={getStatusColor(current)}>{status ? formatStatusLabel(status) : '—'}</Badge>
    );
  }

  const display = pending || current;

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizeOrderStatus(event.target.value);
    if (!next || next === current) return;

    if (DESTRUCTIVE_STATUSES.has(next)) {
      const confirmed = window.confirm(
        `Change booking #${orderId} to ${formatStatusLabel(next)}? This updates the WooCommerce order.`
      );
      if (!confirmed) return;
    }

    setError(null);
    setPending(next);
    try {
      await updateStatus({
        variables: { orderId, status: next },
      });
      setPending(null);
    } catch (err: any) {
      setPending(null);
      const gqlMessage =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message;
      setError(gqlMessage || err?.message || 'Unable to update booking status.');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-1">
        <Input
          type="select"
          bsSize={compact ? 'sm' : undefined}
          value={display}
          disabled={loading}
          onChange={onChange}
          className="fw-medium"
          style={{ minWidth: compact ? 148 : 180 }}
          title="Change booking status"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
        {loading ? <Spinner size="sm" color="primary" /> : null}
      </div>
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
};

export default BookingStatusSelect;
